import { createPredicate } from './condition';
import { TrackingPixel, BrandclubEventType } from './types';
export type DataCollection =
  | AddPaymentInfo
  | AddToCart
  | AddToWishlist
  | CompleteRegistration
  | Contact
  | CustomizeProduct
  | Donate
  | FindLocation
  | InitiateCheckout
  | Lead
  | PageView
  | Purchase
  | Schedule
  | Search
  | StartTrial
  | SubmitApplication
  | Subscribe
  | ViewContent;
export type Fbq = {
  (eventType: EventType, pixelId: string, eventName: string, data: DataCollection, options?: { eventID: string }): void;
  (eventType: EventType, title: string): void;
  (eventType: EventType, title: string, data: DataCollection): void;
  (eventType: EventType, eventName: EventName): void;
  (eventType: EventType, eventName: EventName, data: DataCollection): void;
  (eventType: EventType, title: string, data: boolean, pixelId: string): void;
  (eventType: EventType, title: string, eventName: undefined, options: { eventId: string }): void;
  disablePushState: boolean;
  allowDuplicatePageViews: boolean;
};

export type EventType = 'set' | 'init' | 'consent' | 'track' | 'trackSingle' | 'trackCustom' | 'trackSingleCustom';

export type EventName =
  | 'AddPaymentInfo'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'CompleteRegistration'
  | 'Contact'
  | 'CustomizeProduct'
  | 'Donate'
  | 'FindLocation'
  | 'InitiateCheckout'
  | 'Lead'
  | 'PageView'
  | 'Purchase'
  | 'Schedule'
  | 'Search'
  | 'StartTrial'
  | 'SubmitApplication'
  | 'Subscribe'
  | 'ViewContent';

export type ConfigOptions = {
  pixelId: string;
  autoConfig?: boolean;
};

export type AddPaymentInfo = {
  content_category?: string;
  content_ids?: string[];
  contents?: object[];
  currency?: string;
  value?: number;
};

export type AddToCart = {
  content_ids?: string[];
  content_name?: string;
  content_type?: string;
  product_catalog_id?: string;
  contents?: object[];
  currency?: string;
  value?: number;
};

export type AddToWishlist = {
  content_name?: string;
  content_category?: string;
  content_ids?: string[];
  contents?: object[];
  currency?: string;
  value?: number;
};

export type CompleteRegistration = {
  content_name?: string;
  currency?: string;
  status?: boolean;
  value?: number;
};

export type Unknown = {
  data: any;
};

export type Contact = Unknown;
export type CustomizeProduct = Unknown;
export type Donate = Unknown;
export type FindLocation = Unknown;

export type InitiateCheckout = {
  content_category?: string;
  content_ids?: string[];
  contents?: object[];
  currency?: string;
  num_items?: number;
  value?: number;
};

export type Lead = {
  content_category?: string;
  content_name?: string;
  currency?: string;
  value?: number;
};

export type PageView = Unknown;

export type Purchase = {
  content_ids?: string[];
  content_name?: string;
  content_type?: string;
  contents?: object[];
  currency?: string;
  num_items?: number;
  value?: number;
};

export type Schedule = Unknown;

export type Search = {
  content_category?: string;
  content_ids?: string[];
  contents?: object[];
  currency?: string;
  search_string?: string;
  value?: number;
};

export type StartTrial = {
  currency?: string;
  predicted_ltv?: number;
  value?: number;
};

export type SubmitApplication = Unknown;

export type Subscribe = {
  currency?: string;
  predicted_ltv?: number;
  value?: number;
};

export type ViewContent = {
  content_ids?: string[];
  content_category?: string;
  content_name?: string;
  content_type?: string;
  contents?: object[];
  currency?: string;
  value?: number;
};

declare global {
  interface Window {
    fbq: Fbq;
  }
}

//
let initialized = false;
let debug = false;
const PROVIDER = 'Meta';
let emit = true;
let identity = '';
const randomId = 'id' + Math.random().toString(36).substring(2, 15);

/**
 * Utilities
 */

const warn = (...args: any[]) => {
  if (!debug) {
    return;
  }
  // eslint-disable-next-line no-console
  console.info(...['[meta-pixel]'].concat(args));
};

const log = (...args: any[]) => {
  if (!debug) {
    return;
  }
  // eslint-disable-next-line no-console
  console.info(...['[meta-pixel]'].concat(args));
};

// function getHourId() {
//   const now = new Date();
//   now.setMinutes(0, 0, 0);
//   const hourTimestamp = now.getTime();
//   return hourTimestamp;
// }

const verifyInit = () => {
  if (!initialized) {
    warn('Pixel not initialized before using call ReactPixel.init with required params');
  }
  return initialized;
};

//
const defaultOptions = {
  autoConfig: true,
};

let activeBrandId = '';
const brandPixels: {
  [brandId: string]: {
    pixelId: string;
    eventFilter?: string;
    initialized: boolean;
    eventFilterFn?: (obj: { [key: string]: any }) => boolean;
  }[];
} = {};

function isPixelInitialized(brandId: string, pixelId: string) {
  const pixels = brandPixels?.[brandId];
  if (pixels?.find((item) => item.pixelId === pixelId)?.initialized) {
    return true;
  }
  return false;
}

function setPixelInitialized(brandId: string, pixelId: string) {
  brandPixels[brandId] = brandPixels[brandId].map((item) => {
    if (item.pixelId === pixelId) {
      return {
        ...item, // keep other properties
        pixelId,
        initialized: true,
        eventFilterFn: item.eventFilter ? createPredicate(item.eventFilter) : () => true,
      };
    }
    return item;
  });
}

const metaPixelHelper = {
  setActiveBrand(brand: string) {
    activeBrandId = brand;
  },
  unload() {
    const scriptElement = document.getElementById(randomId);
    if (scriptElement) {
      log('unloading fbq script ...', document.removeChild(scriptElement) ? 'success' : 'failed');
      window.fbq = undefined as never;
    }
  },
  setBrandPixel(brand: string, pixelId: string, eventFilter?: string) {
    if (brand) {
      brandPixels[brand] = brandPixels[brand] || [];
      if (!brandPixels[brand].find((item) => item.pixelId === pixelId) && pixelId) {
        brandPixels[brand].push({
          pixelId,
          eventFilter,
          initialized: false,
        });
      } else {
        // replace
        brandPixels[brand] = brandPixels[brand].map((item) => {
          if (item.pixelId === pixelId) {
            return {
              ...item, // keep other properties
              pixelId,
              eventFilter,
            };
          }
          return item;
        });
      }
    }
  },
  getActivePixels() {
    return activeBrandId ? brandPixels[activeBrandId] : [];
  },
  initializePixel(brandId: string, pixelId: string, options: any) {
    if (isPixelInitialized(brandId, pixelId)) {
      return;
    }
    metaPixelHelper.init(pixelId, identity ? { external_id: identity } : {}, options || defaultOptions);
    setPixelInitialized(brandId, pixelId);
  },
  init(pixelId: string, advancedMatching = {}, options = defaultOptions) {
    initialized = typeof window !== 'undefined' && !!window.fbq;
    if (!initialized) {
      initialized = true;
      log('initializing fbq script');
      (function (
        f: Window & { fbq?: any; _fbq?: any },
        b: Document,
        e: string,
        v: string,
        n?: any,
        t?: HTMLScriptElement,
        s?: Node,
      ) {
        if (f.fbq) return;
        n = f.fbq = function (...args: any[]) {
          n.callMethod ? n.callMethod(...args) : n.queue.push(args);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e) as HTMLScriptElement;
        t.async = !0;
        t.id = randomId;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s?.parentNode?.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }
    if (!pixelId) {
      warn('Please insert pixel id for initializing');
    } else {
      if (options?.autoConfig === false) {
        /*
          The Meta Pixel will send button click and page metadata (such as data structured according to Opengraph or Schema.org formats)
          from your website to improve your ads delivery and measurement and automate your Pixel setup. To configure the Meta Pixel to not send this additional
          information, in the Meta Pixel Base code, add fbq('set', 'autoConfig', 'false', 'FB_PIXEL_ID') above the init call.
        */
        window.fbq('set', 'autoConfig', false, pixelId);
        log(`called fbq('set', 'autoConfig', false, '${pixelId}');`);
      }
      window.fbq('init', pixelId, advancedMatching);
      log(`called fbq('init', '${pixelId}');`);
    }
  },

  trackSingle(title: string, data: DataCollection, isCustom = false) {
    if (!verifyInit()) {
      return;
    }
    const activePixels = metaPixelHelper.getActivePixels();
    const eventType = isCustom ? 'trackSingleCustom' : 'trackSingle';
    if (activePixels && activePixels?.length > 0) {
      activePixels.forEach((pixel) => {
        if (emit) {
          if (pixel.eventFilterFn && !pixel.eventFilterFn(data)) {
            log('eventFilterFn failed', pixel.eventFilterFn, data);
            return;
          }
          window.fbq(eventType, pixel.pixelId, title, data);
        }
        if (debug) {
          log(`called fbq('${eventType}', '${pixel.pixelId}', '${title}'); with data `, data);
        }
      });
    }
  },

  trackSingleCustom(eventName: string, data: DataCollection | Record<string, string>) {
    return metaPixelHelper.trackSingle(eventName, data, true);
  },
};

const metaPixel = {
  enableDebug() {
    debug = true;
  },
  setIdentity(id: string) {
    identity = id;
  },
  enableEmitMetrics(v = true) {
    emit = v;
  },
  track({
    brandId,
    pixels,
    eventName,
    eventType,
    eventPayload
  }: {
    brandId: string;
    pixels: TrackingPixel[];
    eventName: string;
    eventType: BrandclubEventType;
    eventPayload: any;
  }) {
    pixels = pixels?.filter((item) => item.pixelProvider === PROVIDER);
    metaPixelHelper.setActiveBrand(brandId);
    if (Array.isArray(pixels) && pixels?.length > 0) {
      log(JSON.stringify({ brandId, pixels, eventName, payload: eventPayload }));
      if (pixels.length > 0) {
        pixels.forEach((pixel) => {
          metaPixelHelper.setBrandPixel(brandId, pixel.pixelId, pixel.eventFilter);
          metaPixelHelper.initializePixel(brandId, pixel.pixelId, pixel.pixelOptions);
        });
      }
      if (eventType === BrandclubEventType.OrderSubmitted || eventType === BrandclubEventType.AddToCart) {
        eventPayload = { ...eventPayload }; // clone
        if (eventPayload.retailPrice) {
          eventPayload.value = eventPayload.retailPrice;
          eventPayload.currency = 'USD';
        }
        eventPayload.retailPrice = undefined;
      }
      metaPixelHelper.trackSingleCustom(eventName, eventPayload);
    } else {
      metaPixelHelper.unload();
    }
  },

  grantConsent() {
    if (!verifyInit()) {
      return;
    }

    window.fbq('consent', 'grant');

    if (debug) {
      log(`called fbq('consent', 'grant');`);
    }
  },

  revokeConsent() {
    if (!verifyInit()) {
      return;
    }

    window.fbq('consent', 'revoke');

    if (debug) {
      log(`called fbq('consent', 'revoke');`);
    }
  },
};

export default metaPixel;
